import { useEffect, useState } from "react";
import { api, fmtPrice } from "../../lib/api";
import { useBusiness } from "../../context/BusinessContext";
import { toast } from "sonner";
import { X } from "lucide-react";

const STATUSES = ["pending", "pending_verification", "paid", "failed", "cancelled"];

export default function AdminOrders() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const { business } = useBusiness();

  const refresh = () => {
    const params = filter ? { status_filter: filter } : {};
    api.get("/orders", { params }).then((r) => setItems(r.data));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const changeStatus = async (order, status) => {
    await api.put(`/orders/${order.id}/status`, { status });
    toast.success("Order updated");
    refresh();
    if (selected && selected.id === order.id) setSelected({ ...selected, payment_status: status });
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-ultra text-neutral-500">Fulfilment</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter mt-2">Orders</h1>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-ultra text-neutral-500">Filter</span>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border-b border-black bg-transparent py-2 text-sm outline-none"
            data-testid="orders-filter-select"
          >
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-10 bg-white border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] text-white">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-ultra">Order</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-ultra">Customer</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-ultra">Method</th>
              <th className="text-right px-4 py-3 text-[10px] uppercase tracking-ultra">Total</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-ultra">Status</th>
              <th className="text-right px-4 py-3 text-[10px] uppercase tracking-ultra">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/10">
            {items.map((o) => (
              <tr key={o.id} data-testid={`admin-order-row-${o.id}`}>
                <td className="px-4 py-3 font-mono text-xs">#{o.id.slice(0, 8).toUpperCase()}</td>
                <td className="px-4 py-3">
                  <div className="font-medium">{o.customer_name}</div>
                  <div className="text-xs text-neutral-500">{o.customer_email}</div>
                </td>
                <td className="px-4 py-3 uppercase text-[10px] tracking-ultra">
                  {o.payment_method}
                </td>
                <td className="px-4 py-3 text-right font-medium">
                  {fmtPrice(o.total_amount, business.currency)}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={o.payment_status} />
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => setSelected(o)}
                    className="text-[10px] uppercase tracking-ultra border border-black px-3 py-1 hover:bg-black hover:text-white"
                    data-testid={`order-details-${o.id}`}
                  >
                    Details
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan="6" className="px-4 py-10 text-center text-neutral-500 text-sm">
                  No orders.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-auto p-4" data-testid="order-details-modal">
          <div className="bg-white w-full max-w-2xl mt-10 p-8 border border-black">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-[10px] uppercase tracking-ultra text-neutral-500">Order</div>
                <h2 className="text-2xl font-black tracking-tighter">
                  #{selected.id.slice(0, 8).toUpperCase()}
                </h2>
              </div>
              <button onClick={() => setSelected(null)} className="p-2" data-testid="order-details-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-ultra text-neutral-500 mb-1">Customer</div>
                <div>{selected.customer_name}</div>
                <div className="text-neutral-500">{selected.customer_email}</div>
                <div className="text-neutral-500">{selected.customer_phone}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-ultra text-neutral-500 mb-1">Address</div>
                <div>{selected.address || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-ultra text-neutral-500 mb-1">Method</div>
                <div className="uppercase">{selected.payment_method}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-ultra text-neutral-500 mb-1">Status</div>
                <StatusBadge status={selected.payment_status} />
              </div>
            </div>

            <div className="mt-6 border-t border-black/10 pt-4">
              <div className="text-[10px] uppercase tracking-ultra text-neutral-500 mb-2">Items</div>
              <div className="divide-y divide-black/10">
                {selected.items.map((it) => (
                  <div key={it.product_id} className="flex justify-between py-2 text-sm">
                    <div>
                      <span className="font-medium">{it.name}</span>{" "}
                      <span className="text-neutral-500">×{it.quantity}</span>
                    </div>
                    <div>{fmtPrice(it.price * it.quantity, business.currency)}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-black flex justify-between font-bold">
                <span>Total</span>
                <span>{fmtPrice(selected.total_amount, business.currency)}</span>
              </div>
            </div>

            {selected.qr_receipt_image && (
              <div className="mt-6">
                <div className="text-[10px] uppercase tracking-ultra text-neutral-500 mb-2">QR receipt</div>
                <img src={selected.qr_receipt_image} alt="Receipt" className="border border-black/10 max-h-80" />
              </div>
            )}

            <div className="mt-8 border-t border-black pt-6">
              <div className="text-[10px] uppercase tracking-ultra text-neutral-500 mb-2">Update status</div>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => changeStatus(selected, s)}
                    className={`text-[10px] uppercase tracking-ultra border px-3 py-2 ${
                      selected.payment_status === s
                        ? "bg-black text-white border-black"
                        : "border-black hover:bg-black hover:text-white"
                    }`}
                    data-testid={`order-set-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid: "bg-black text-white",
    failed: "bg-accent text-white",
    pending_verification: "bg-yellow-300 text-black",
    cancelled: "bg-neutral-300 text-neutral-600",
    pending: "bg-neutral-200 text-neutral-700",
  };
  return (
    <span className={`px-2 py-1 text-[10px] uppercase tracking-widest ${map[status] || "bg-neutral-200 text-neutral-700"}`}>
      {status}
    </span>
  );
}
